<template>
  <div class="animated fadeIn">
    <b-card>
      <div slot="header">
        <i class="fa fa-user-graduate"></i>
        <strong>Designar Curso</strong>
        <small>Atribuir cursos para funcionários da empresa</small>
      </div>

      <b-form @submit.prevent="submitAssignment">
        <!-- Seleção de Curso -->
        <b-row>
          <b-col md="12">
            <b-form-group
              label="Curso *"
              label-for="course-select"
              description="Selecione o curso que será atribuído aos participantes"
            >
              <b-form-select
                id="course-select"
                v-model="form.courseId"
                :options="courseOptions"
                required
                :disabled="loadingCourses"
              >
                <template #first>
                  <b-form-select-option :value="null" disabled>
                    {{
                      loadingCourses
                        ? "Carregando cursos..."
                        : "-- Selecione um curso --"
                    }}
                  </b-form-select-option>
                </template>
              </b-form-select>
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Tipo de Seleção -->
        <b-row>
          <b-col md="12">
            <b-form-group
              label="Tipo de Designação *"
              label-for="selection-type"
              description="Escolha como selecionar os participantes"
            >
              <b-form-radio-group
                id="selection-type"
                v-model="form.selectionType"
                :options="selectionTypeOptions"
                stacked
                @change="resetSelection"
              ></b-form-radio-group>
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Seleção Individual -->
        <b-row v-if="form.selectionType === 'individual'">
          <b-col md="12">
            <b-form-group
              label="Funcionários *"
              label-for="employee-select"
              description="Selecione os funcionários que farão o curso"
            >
              <div class="mb-2">
                <b-button
                  variant="outline-primary"
                  size="sm"
                  @click="showEmployeeModal = true"
                >
                  <i class="fa fa-search"></i> Buscar Funcionários
                </b-button>
              </div>

              <b-list-group v-if="selectedEmployees.length > 0">
                <b-list-group-item
                  v-for="emp in selectedEmployees"
                  :key="emp._id"
                  class="d-flex justify-content-between align-items-center"
                >
                  <div>
                    <strong>{{ emp.name }} {{ emp.surname }}</strong>
                    <br />
                    <small class="text-muted">{{ emp.email }}</small>
                  </div>
                  <b-button
                    variant="danger"
                    size="sm"
                    @click="removeEmployee(emp._id)"
                  >
                    <i class="fa fa-times"></i>
                  </b-button>
                </b-list-group-item>
              </b-list-group>
              <div v-else class="text-muted">
                Nenhum funcionário selecionado
              </div>
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Seleção por Departamento -->
        <b-row v-if="form.selectionType === 'department'">
          <b-col md="12">
            <b-form-group
              label="Área/Departamento *"
              label-for="department-select"
              description="Todos os funcionários desta área receberão o curso"
            >
              <b-form-select
                id="department-select"
                v-model="form.departmentId"
                :options="departmentOptions"
                required
                :disabled="loadingDepartments"
                @change="previewDepartmentEmployees"
              >
                <template #first>
                  <b-form-select-option :value="null" disabled>
                    {{
                      loadingDepartments
                        ? "Carregando..."
                        : "-- Selecione uma área/departamento --"
                    }}
                  </b-form-select-option>
                </template>
              </b-form-select>
            </b-form-group>

            <!-- Preview de funcionários do departamento -->
            <b-alert v-if="previewEmployees.length > 0" show variant="info">
              <strong>{{ previewEmployees.length }}</strong> funcionário(s)
              será(ão) matriculado(s)
              <b-button
                variant="link"
                size="sm"
                @click="showPreviewModal = true"
              >
                Ver lista
              </b-button>
            </b-alert>
          </b-col>
        </b-row>

        <!-- Seleção por Unidade de Negócio -->
        <b-row v-if="form.selectionType === 'businessUnit'">
          <b-col md="12">
            <b-form-group
              label="Cargo *"
              label-for="business-unit-select"
              description="Todos os funcionários com este cargo receberão o curso"
            >
              <b-form-select
                id="business-unit-select"
                v-model="form.businessUnitId"
                :options="businessUnitOptions"
                required
                :disabled="loadingBusinessUnits"
                @change="previewBusinessUnitEmployees"
              >
                <template #first>
                  <b-form-select-option :value="null" disabled>
                    {{
                      loadingBusinessUnits
                        ? "Carregando..."
                        : "-- Selecione um cargo --"
                    }}
                  </b-form-select-option>
                </template>
              </b-form-select>
            </b-form-group>

            <!-- Preview de funcionários da unidade -->
            <b-alert v-if="previewEmployees.length > 0" show variant="info">
              <strong>{{ previewEmployees.length }}</strong> funcionário(s)
              será(ão) matriculado(s)
              <b-button
                variant="link"
                size="sm"
                @click="showPreviewModal = true"
              >
                Ver lista
              </b-button>
            </b-alert>
          </b-col>
        </b-row>

        <!-- Opções Adicionais -->
        <b-row>
          <b-col md="6">
            <b-form-group
              label="Prazo (opcional)"
              label-for="deadline"
              description="Data limite para conclusão do curso"
            >
              <b-form-input
                id="deadline"
                v-model="form.deadline"
                type="date"
              ></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group>
              <b-form-checkbox v-model="form.notifyByEmail" class="mt-4">
                Enviar notificação por e-mail aos participantes
              </b-form-checkbox>
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Opção de Criar Turma -->
        <b-row>
          <b-col md="12">
            <hr />
            <b-form-group>
              <b-form-checkbox
                v-model="form.createClass"
                @change="onCreateClassChange"
              >
                <strong>Criar Turma</strong>
                <small class="text-muted d-block">
                  Agrupar esses alunos em uma turma para acompanhamento de
                  métricas e estatísticas coletivas
                </small>
              </b-form-checkbox>
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Dados da Turma (se criar turma estiver marcado) -->
        <b-row v-if="form.createClass">
          <b-col md="12">
            <b-card bg-variant="light" class="mb-3">
              <h6 class="mb-3"><i class="fa fa-users"></i> Dados da Turma</h6>

              <b-row>
                <b-col md="12">
                  <b-form-group
                    label="Nome da Turma *"
                    label-for="class-name"
                    description="Nome identificador para esta turma"
                  >
                    <b-form-input
                      id="class-name"
                      v-model="form.classData.name"
                      placeholder="Ex: Turma Ética Q1 2025"
                      :required="form.createClass"
                    ></b-form-input>
                  </b-form-group>
                </b-col>
              </b-row>

              <b-row>
                <b-col md="12">
                  <b-form-group
                    label="Descrição (opcional)"
                    label-for="class-description"
                    description="Informações adicionais sobre esta turma"
                  >
                    <b-form-textarea
                      id="class-description"
                      v-model="form.classData.description"
                      rows="3"
                      placeholder="Ex: Turma para funcionários do departamento de vendas - primeiro trimestre de 2025"
                    ></b-form-textarea>
                  </b-form-group>
                </b-col>
              </b-row>

              <b-row>
                <b-col md="6">
                  <b-form-group
                    label="Data de Início (opcional)"
                    label-for="class-start-date"
                    description="Quando a turma inicia"
                  >
                    <b-form-input
                      id="class-start-date"
                      v-model="form.classData.startDate"
                      type="date"
                    ></b-form-input>
                  </b-form-group>
                </b-col>

                <b-col md="6">
                  <b-form-group
                    label="Data de Término (opcional)"
                    label-for="class-end-date"
                    description="Quando a turma encerra"
                  >
                    <b-form-input
                      id="class-end-date"
                      v-model="form.classData.endDate"
                      type="date"
                      :min="form.classData.startDate"
                    ></b-form-input>
                  </b-form-group>
                </b-col>
              </b-row>
            </b-card>
          </b-col>
        </b-row>

        <!-- Botões de Ação -->
        <b-row>
          <b-col md="12" class="text-right">
            <b-button
              variant="secondary"
              @click="$router.push('/ead')"
              :disabled="submitting"
            >
              Cancelar
            </b-button>
            <b-button
              type="submit"
              variant="primary"
              :disabled="!canSubmit || submitting"
              class="ml-2"
            >
              <i class="fa fa-check"></i>
              {{ submitting ? "Processando..." : "Designar Curso" }}
            </b-button>
          </b-col>
        </b-row>
      </b-form>
    </b-card>

    <!-- Modal de Seleção de Funcionários -->
    <b-modal
      v-model="showEmployeeModal"
      title="Selecionar Funcionários"
      size="lg"
      @ok="confirmEmployeeSelection"
    >
      <b-form-group label="Buscar" label-for="employee-search">
        <b-form-input
          id="employee-search"
          v-model="employeeSearch"
          placeholder="Digite o nome ou email do funcionário..."
          @input="searchEmployees"
        ></b-form-input>
      </b-form-group>

      <b-table
        :items="filteredEmployees"
        :fields="employeeTableFields"
        :busy="loadingEmployees"
        select-mode="multi"
        selectable
        @row-selected="onEmployeeRowSelected"
        ref="employeeTable"
        striped
        hover
        responsive
      >
        <template #cell(selected)="{ rowSelected }">
          <template v-if="rowSelected">
            <i class="fa fa-check text-success"></i>
          </template>
          <template v-else>
            <i class="fa fa-square-o text-muted"></i>
          </template>
        </template>

        <template #table-busy>
          <div class="text-center text-primary my-2">
            <b-spinner class="align-middle"></b-spinner>
            <strong>Carregando...</strong>
          </div>
        </template>
      </b-table>
    </b-modal>

    <!-- Modal de Preview de Funcionários -->
    <b-modal
      v-model="showPreviewModal"
      title="Funcionários que serão matriculados"
      size="lg"
      ok-only
    >
      <b-table
        :items="previewEmployees"
        :fields="previewTableFields"
        striped
        hover
        responsive
      ></b-table>
    </b-modal>

    <!-- Modal de Resultado -->
    <b-modal
      v-model="showResultModal"
      :title="resultModalTitle"
      size="lg"
      ok-only
      @hidden="handleResultModalClose"
    >
      <div v-if="assignmentResults">
        <b-alert
          show
          variant="success"
          v-if="assignmentResults.success.length > 0"
        >
          <h5>
            ✓ Matrículas Realizadas com Sucesso ({{
              assignmentResults.success.length
            }})
          </h5>
          <ul>
            <li
              v-for="result in assignmentResults.success"
              :key="result.enrollmentId"
            >
              {{ result.name }} ({{ result.email }})
              <b-badge v-if="result.isNewUser" variant="info"
                >Novo Usuário</b-badge
              >
            </li>
          </ul>
        </b-alert>

        <b-alert
          show
          variant="warning"
          v-if="
            assignmentResults.skipped && assignmentResults.skipped.length > 0
          "
        >
          <h5>
            ⚠ Funcionários Ignorados ({{ assignmentResults.skipped.length }})
          </h5>
          <ul>
            <li
              v-for="skipped in assignmentResults.skipped"
              :key="skipped.employeeId"
            >
              {{ skipped.name }} ({{ skipped.email }}): {{ skipped.reason }}
            </li>
          </ul>
        </b-alert>

        <b-alert
          show
          variant="danger"
          v-if="assignmentResults.errors.length > 0"
        >
          <h5>✗ Erros ({{ assignmentResults.errors.length }})</h5>
          <ul>
            <li
              v-for="error in assignmentResults.errors"
              :key="error.employeeId"
            >
              {{ error.name }} ({{ error.email }}): {{ error.error }}
            </li>
          </ul>
        </b-alert>
      </div>
    </b-modal>

    <!-- Modal de Validação antes de matricular -->
    <b-modal
      v-model="showValidationModal"
      title="Confirmar Designação de Curso"
      size="xl"
      @ok="confirmAndSubmit"
      @cancel="cancelValidation"
      ok-title="Confirmar e Matricular"
      cancel-title="Cancelar"
    >
      <div v-if="validationResults">
        <!-- Funcionários que serão matriculados -->
        <b-alert
          show
          variant="success"
          v-if="validationResults.canEnroll.length > 0"
        >
          <h5 class="mb-3">
            <i class="fa fa-check-circle"></i>
            Funcionários que serão matriculados ({{
              validationResults.canEnroll.length
            }})
          </h5>
          <b-table
            :items="validationResults.canEnroll"
            :fields="validationTableFields"
            striped
            hover
            small
            responsive
          ></b-table>
        </b-alert>

        <!-- Funcionários já matriculados -->
        <b-alert
          show
          variant="warning"
          v-if="validationResults.alreadyEnrolled.length > 0"
        >
          <h5 class="mb-3">
            <i class="fa fa-exclamation-triangle"></i>
            Já matriculados neste curso ({{
              validationResults.alreadyEnrolled.length
            }})
          </h5>
          <p class="mb-2">
            <small>Estes funcionários não serão matriculados novamente.</small>
          </p>
          <b-table
            :items="validationResults.alreadyEnrolled"
            :fields="alreadyEnrolledFields"
            striped
            hover
            small
            responsive
          ></b-table>
        </b-alert>

        <!-- Funcionários com certificado válido -->
        <b-alert
          show
          variant="info"
          v-if="validationResults.hasCertificate.length > 0"
        >
          <h5 class="mb-3">
            <i class="fa fa-certificate"></i>
            Possuem certificado válido ({{
              validationResults.hasCertificate.length
            }})
          </h5>
          <p class="mb-2">
            <small
              >Estes funcionários já concluíram o curso e possuem certificado
              válido por 1 ano.</small
            >
          </p>
          <b-table
            :items="validationResults.hasCertificate"
            :fields="certificateFields"
            striped
            hover
            small
            responsive
          >
            <template #cell(validUntil)="data">
              {{ formatDate(data.value) }}
            </template>
            <template #cell(daysRemaining)="data">
              <b-badge variant="success">{{ data.value }} dias</b-badge>
            </template>
          </b-table>
        </b-alert>

        <!-- Aviso se nenhum funcionário pode ser matriculado -->
        <b-alert
          show
          variant="danger"
          v-if="validationResults.canEnroll.length === 0"
        >
          <h5>
            <i class="fa fa-times-circle"></i>
            Nenhum funcionário pode ser matriculado
          </h5>
          <p>
            Todos os funcionários selecionados já estão matriculados ou possuem
            certificado válido para este curso.
          </p>
        </b-alert>
      </div>

      <div v-else class="text-center py-4">
        <b-spinner></b-spinner>
        <p class="mt-2">Validando elegibilidade dos funcionários...</p>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  getEmployees,
  getDepartments,
  getBusinessUnits,
  getCourses,
  assignCourse,
  validateEnrollments,
} from "@/api/course-assignment";

export default {
  name: "DesignarCurso",
  data() {
    return {
      form: {
        courseId: null,
        selectionType: "individual",
        employeeIds: [],
        departmentId: null,
        businessUnitId: null,
        deadline: null,
        notifyByEmail: true,
        createClass: false,
        classData: {
          name: "",
          description: "",
          startDate: null,
          endDate: null,
        },
      },
      courses: [],
      employees: [],
      departments: [],
      businessUnits: [],
      selectedEmployees: [],
      previewEmployees: [],
      employeeSearch: "",
      tempSelectedEmployees: [],
      loadingCourses: false,
      loadingEmployees: false,
      loadingDepartments: false,
      loadingBusinessUnits: false,
      submitting: false,
      showEmployeeModal: false,
      showPreviewModal: false,
      showResultModal: false,
      showValidationModal: false,
      assignmentResults: null,
      validationResults: null,
      pendingPayload: null,
      selectionTypeOptions: [
        { text: "Funcionários Específicos", value: "individual" },
        { text: "Área/Departamento Completo", value: "department" },
        { text: "Cargo Completo", value: "businessUnit" },
      ],
      employeeTableFields: [
        { key: "selected", label: "" },
        { key: "name", label: "Nome", sortable: true },
        { key: "email", label: "Email", sortable: true },
        { key: "position", label: "Cargo" },
      ],
      previewTableFields: [
        { key: "name", label: "Nome", sortable: true },
        { key: "email", label: "Email", sortable: true },
        { key: "position", label: "Cargo" },
      ],
      validationTableFields: [
        { key: "name", label: "Nome", sortable: true },
        { key: "email", label: "Email", sortable: true },
        { key: "position", label: "Cargo" },
        { key: "area", label: "Área" },
      ],
      alreadyEnrolledFields: [
        { key: "name", label: "Nome", sortable: true },
        { key: "email", label: "Email", sortable: true },
        { key: "position", label: "Cargo" },
      ],
      certificateFields: [
        { key: "name", label: "Nome", sortable: true },
        { key: "email", label: "Email", sortable: true },
        { key: "certificateNumber", label: "Certificado" },
        { key: "validUntil", label: "Válido até" },
        { key: "daysRemaining", label: "Dias restantes" },
      ],
    };
  },
  computed: {
    courseOptions() {
      return this.courses.map((course) => ({
        value: course._id,
        text: course.nome,
      }));
    },
    departmentOptions() {
      return this.departments.map((dept) => ({
        value: dept._id,
        text: dept.value,
      }));
    },
    businessUnitOptions() {
      return this.businessUnits.map((unit) => ({
        value: unit._id,
        text: unit.value,
      }));
    },
    filteredEmployees() {
      if (!this.employeeSearch) {
        return this.employees;
      }
      const search = this.employeeSearch.toLowerCase();
      return this.employees.filter(
        (emp) =>
          emp.name.toLowerCase().includes(search) ||
          emp.email.toLowerCase().includes(search)
      );
    },
    canSubmit() {
      if (!this.form.courseId) return false;

      // Se criar turma, validar nome da turma
      if (this.form.createClass && !this.form.classData.name) return false;

      if (this.form.selectionType === "individual") {
        return this.selectedEmployees.length > 0;
      } else if (this.form.selectionType === "department") {
        return this.form.departmentId !== null;
      } else if (this.form.selectionType === "businessUnit") {
        return this.form.businessUnitId !== null;
      }

      return false;
    },
    resultModalTitle() {
      if (!this.assignmentResults) return "Resultado";
      return `Designação Concluída - ${this.assignmentResults.success.length}/${this.assignmentResults.total}`;
    },
  },
  mounted() {
    this.loadCourses();
    this.loadEmployees();
    this.loadDepartments();
    this.loadBusinessUnits();
  },
  methods: {
    async loadCourses() {
      this.loadingCourses = true;
      try {
        const response = await getCourses();
        this.courses = response.data;
        console.log("📚 Cursos carregados:", this.courses.length, this.courses);
      } catch (error) {
        console.error("Erro ao carregar cursos:", error);
        this.$toasted.error("Erro ao carregar cursos");
      } finally {
        this.loadingCourses = false;
      }
    },
    async loadEmployees() {
      this.loadingEmployees = true;
      try {
        const response = await getEmployees();
        this.employees = response.data.map((emp) => ({
          ...emp,
          name: `${emp.name} ${emp.surname || ""}`.trim(),
        }));
        console.log("👥 Funcionários carregados:", this.employees.length);
      } catch (error) {
        console.error("Erro ao carregar funcionários:", error);
        this.$toasted.error("Erro ao carregar funcionários");
      } finally {
        this.loadingEmployees = false;
      }
    },
    async loadDepartments() {
      this.loadingDepartments = true;
      try {
        const response = await getDepartments();
        this.departments = response.data;
        console.log(
          "🏢 Departamentos carregados:",
          this.departments.length,
          this.departments
        );
      } catch (error) {
        console.error("Erro ao carregar departamentos:", error);
        this.$toasted.error("Erro ao carregar departamentos");
      } finally {
        this.loadingDepartments = false;
      }
    },
    async loadBusinessUnits() {
      this.loadingBusinessUnits = true;
      try {
        const response = await getBusinessUnits();
        this.businessUnits = response.data;
        console.log(
          "🏭 Unidades de negócio carregadas:",
          this.businessUnits.length,
          this.businessUnits
        );
      } catch (error) {
        console.error("Erro ao carregar unidades de negócio:", error);
        this.$toasted.error("Erro ao carregar unidades de negócio");
      } finally {
        this.loadingBusinessUnits = false;
      }
    },
    onCreateClassChange(checked) {
      if (checked && !this.form.classData.name) {
        // Gerar nome padrão sugerido
        const course = this.courses.find((c) => c._id === this.form.courseId);
        if (course) {
          const today = new Date();
          this.form.classData.name = `Turma ${course.nome.substring(
            0,
            30
          )} - ${today.toLocaleDateString("pt-BR")}`;
        }
      }
    },
    resetSelection() {
      this.form.employeeIds = [];
      this.form.departmentId = null;
      this.form.businessUnitId = null;
      this.selectedEmployees = [];
      this.previewEmployees = [];
    },
    searchEmployees() {
      // Filtragem reativa já implementada via computed
    },
    onEmployeeRowSelected(items) {
      this.tempSelectedEmployees = items;
    },
    confirmEmployeeSelection() {
      this.selectedEmployees = [...this.tempSelectedEmployees];
      this.form.employeeIds = this.selectedEmployees.map((emp) => emp._id);
    },
    removeEmployee(employeeId) {
      this.selectedEmployees = this.selectedEmployees.filter(
        (emp) => emp._id !== employeeId
      );
      this.form.employeeIds = this.selectedEmployees.map((emp) => emp._id);
    },
    async previewDepartmentEmployees() {
      if (!this.form.departmentId) {
        this.previewEmployees = [];
        return;
      }

      try {
        const response = await getEmployees({
          department: this.form.departmentId,
        });
        this.previewEmployees = response.data.map((emp) => ({
          ...emp,
          name: `${emp.name} ${emp.surname || ""}`.trim(),
        }));
      } catch (error) {
        console.error("Erro ao buscar funcionários do departamento:", error);
        this.$toasted.error("Erro ao buscar funcionários");
      }
    },
    async previewBusinessUnitEmployees() {
      if (!this.form.businessUnitId) {
        this.previewEmployees = [];
        return;
      }

      try {
        const response = await getEmployees({
          businessUnit: this.form.businessUnitId,
        });
        this.previewEmployees = response.data.map((emp) => ({
          ...emp,
          name: `${emp.name} ${emp.surname || ""}`.trim(),
        }));
      } catch (error) {
        console.error("Erro ao buscar funcionários da unidade:", error);
        this.$toasted.error("Erro ao buscar funcionários");
      }
    },
    async submitAssignment() {
      // Primeiro validar elegibilidade
      this.validationResults = null;
      this.showValidationModal = true;

      try {
        const payload = {
          courseId: this.form.courseId,
          participants: {},
        };

        if (this.form.selectionType === "individual") {
          payload.participants = {
            type: "individual",
            employeeIds: this.form.employeeIds,
          };
        } else if (this.form.selectionType === "department") {
          payload.participants = {
            type: "department",
            departmentId: this.form.departmentId,
          };
        } else if (this.form.selectionType === "businessUnit") {
          payload.participants = {
            type: "businessUnit",
            businessUnitId: this.form.businessUnitId,
          };
        }

        // Guardar payload para uso posterior
        this.pendingPayload = {
          ...payload,
          deadline: this.form.deadline || undefined,
          notifyByEmail: this.form.notifyByEmail,
          createClass: this.form.createClass,
          classData: this.form.createClass ? this.form.classData : undefined,
        };

        // Validar elegibilidade
        const response = await validateEnrollments(payload);
        this.validationResults = response.data;
      } catch (error) {
        console.error("Erro ao validar matrículas:", error);
        const errorMsg =
          error.response?.data?.error || "Erro ao validar matrículas";
        this.$toasted.error(errorMsg);
        this.showValidationModal = false;
      }
    },
    cancelValidation() {
      this.validationResults = null;
      this.pendingPayload = null;
    },
    async confirmAndSubmit() {
      // Verificar se há funcionários para matricular
      if (
        !this.validationResults ||
        this.validationResults.canEnroll.length === 0
      ) {
        this.$toasted.warning("Nenhum funcionário elegível para matrícula");
        return;
      }

      this.submitting = true;
      this.showValidationModal = false;

      try {
        const response = await assignCourse(this.pendingPayload);

        this.assignmentResults = response.data.results;
        this.showResultModal = true;
      } catch (error) {
        console.error("Erro ao designar curso:", error);
        const errorMsg =
          error.response?.data?.error || "Erro ao designar curso";
        this.$toasted.error(errorMsg);
      } finally {
        this.submitting = false;
        this.pendingPayload = null;
        this.validationResults = null;
      }
    },
    formatDate(dateString) {
      if (!dateString) return "-";
      const date = new Date(dateString);
      return date.toLocaleDateString("pt-BR");
    },
    handleResultModalClose() {
      // Apenas fechar o modal e limpar o formulário
      // Não redirecionar para dashboard
      if (this.assignmentResults?.success.length > 0) {
        // Verificar se foi criada uma turma
        const classId = this.assignmentResults.classId;
        if (classId) {
          this.$toasted.success(
            `Turma "${this.assignmentResults.className}" criada com sucesso!`
          );
        }

        // Limpar formulário para nova designação
        this.form.courseId = null;
        this.form.selectionType = "individual";
        this.form.employeeIds = [];
        this.form.departmentId = null;
        this.form.businessUnitId = null;
        this.form.deadline = null;
        this.form.notifyByEmail = true;
        this.form.createClass = false;
        this.form.classData = {
          name: "",
          description: "",
          startDate: null,
          endDate: null,
        };
        this.selectedEmployees = [];
        this.previewEmployees = [];
        this.assignmentResults = null;
      }
    },
  },
};
</script>

<style scoped>
.action-button {
  height: 100px;
}

.action-button i {
  color: inherit;
}
</style>
